// src/pages/AgencyPartnership.jsx
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "react-hot-toast";
import { notifyApiError } from "../utils/apiError";
import {
  Trash,
  PlusCircle,
  Send,
  Mail,
  Phone,
  User,
  Users,
  Building,
  Globe,
  DollarSign,
  Briefcase,
  Handshake,
  Shield,
  TrendingUp,
  Award,
  CheckCircle,
  Star,
  ArrowRight,
} from "lucide-react";
import axios from "axios";
import transportImg from "../assets/transport.png";
import { useAuth } from "../contexts/AuthContext";
import zelijPattern from "../assets/zelij3.png";

// ✅ i18n helper
import { t as tr } from "../contexts/translations";

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "https://nouzhaback-theta.vercel.app").replace(/\/$/, "");

const getAuthToken = () =>
  localStorage.getItem("token") ||
  localStorage.getItem("authToken") ||
  localStorage.getItem("accessToken") ||
  "";

const api = axios.create({ baseURL: API_BASE });
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});


const AgencyPartnership = ({ language }) => {
  const { currentUser } = useAuth();

  const T = (key, fallback) => {
    const v = tr(key, language);
    return v === key ? fallback : v;
  };

  const benefits = useMemo(
    () => [
      {
        icon: <TrendingUp className="w-6 h-6 text-white" />,
        title: T("agencyBenefit1Title", "Croissance garantie"),
        description: T("agencyBenefit1Desc", "Augmentez votre volume d'affaires avec notre réseau au Maroc."),
        gradient: "from-orange-500 to-amber-500",
      },
      {
        icon: <Shield className="w-6 h-6 text-white" />,
        title: T("agencyBenefit2Title", "Fiabilité totale"),
        description: T("agencyBenefit2Desc", "Partenaire certifié avec des standards de qualité exigeants."),
        gradient: "from-amber-500 to-orange-600",
      },
      {
        icon: <Award className="w-6 h-6 text-white" />,
        title: T("agencyBenefit3Title", "Support dédié"),
        description: T("agencyBenefit3Desc", "Un account manager personnel pour chaque partenaire agence."),
        gradient: "from-orange-600 to-red-500",
      },
      {
        icon: <DollarSign className="w-6 h-6 text-white" />,
        title: T("agencyBenefit4Title", "Commissions attractives"),
        description: T("agencyBenefit4Desc", "Des marges compétitives qui récompensent votre fidélité."),
        gradient: "from-red-500 to-orange-500",
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language]
  );

  const partnerStats = useMemo(
    () => [
      { value: "150+", label: T("agencyStatPartners", "Agences partenaires") },
      { value: "35", label: T("agencyStatCountries", "Pays représentés") },
      { value: "98%", label: T("agencyStatSatisfaction", "Taux de satisfaction") },
      { value: "24h", label: T("agencyStatResponse", "Temps de réponse") },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language]
  );

  const partnershipSteps = useMemo(
    () => [
      { step: "01", title: T("agencyStep1Title", "Candidature"), desc: T("agencyStep1Desc", "Remplissez le formulaire ci-dessous") },
      { step: "02", title: T("agencyStep2Title", "Évaluation"), desc: T("agencyStep2Desc", "Notre équipe étudie votre profil") },
      { step: "03", title: T("agencyStep3Title", "Collaboration"), desc: T("agencyStep3Desc", "Démarrez votre partenariat") },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language]
  );

  const partnerTestimonials = useMemo(
    () => [
      {
        quote: T("agencyTestimonial1Quote", "Le meilleur partenaire au Maroc. Un service impeccable et des réponses ultra-rapides."),
        name: "Jean-Marc D.",
        agency: T("agencyTestimonial1Agency", "Voyages Horizon, France"),
        rating: 5,
      },
      {
        quote: T("agencyTestimonial2Quote", "Grâce à Nouzha, nous avons doublé notre offre Maroc en un an. Très professionnel."),
        name: "Maria G.",
        agency: T("agencyTestimonial2Agency", "Sol y Luna Travel, Espagne"),
        rating: 5,
      },
      {
        quote: T("agencyTestimonial3Quote", "Collaboration fluide, commissions attractives et qualité constante. Je recommande."),
        name: "Thomas K.",
        agency: T("agencyTestimonial3Agency", "Wanderlust Tours, Allemagne"),
        rating: 5,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language]
  );

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [email, setEmail] = useState(currentUser?.email || "");
  const [nom, setNom] = useState(currentUser?.nom || "");
  const [prenom, setPrenom] = useState(currentUser?.prenom || "");
  const [telephone, setTelephone] = useState(currentUser?.telephone || "");

  const [agencyInfo, setAgencyInfo] = useState({
    nom_agence: "",
    pays_origine: "",
    site_web: "",
    licence_numero: "",
    nb_clients_annuel: "",
    type_services: [],
    commission_souhaitee: "",
    volume_affaires_estime: "",
    experience_maroc: "",
    langues_parlees: [],
    presentation_agence: "",
  });

  const [servicesRequis, setServicesRequis] = useState([
    { type_service: "", description: "", volume_estime: "", periode: "" },
  ]);

  useEffect(() => {
    setEmail(currentUser?.email || "");
    setNom(currentUser?.nom || "");
    setPrenom(currentUser?.prenom || "");
    setTelephone(currentUser?.telephone || "");
  }, [currentUser]);

  const typesServices = useMemo(
    () => [
      { value: "Hébergement (Hôtels/Riads)", label: T("agencyServiceTypeAccommodation", "Hébergement (Hôtels/Riads)") },
      { value: "Circuits organisés", label: T("agencyServiceTypeCircuits", "Circuits organisés") },
      { value: "Transport touristique", label: T("agencyServiceTypeTransport", "Transport touristique") },
      { value: "Guides touristiques", label: T("agencyServiceTypeGuides", "Guides touristiques") },
      { value: "Location de véhicules", label: T("agencyServiceTypeCarRental", "Location de véhicules") },
      { value: "Visites guidées", label: T("agencyServiceTypeGuidedTours", "Visites guidées") },
      { value: "Activités d'aventure", label: T("agencyServiceTypeAdventure", "Activités d'aventure") },
      { value: "Événements et MICE", label: T("agencyServiceTypeEvents", "Événements et MICE") },
      { value: "Transferts aéroport", label: T("agencyServiceTypeTransfers", "Transferts aéroport") },
      { value: "Assistance 24/7", label: T("agencyServiceTypeAssistance", "Assistance 24/7") },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language]
  );

  const languesDisponibles = useMemo(
    () => [
      { value: "Français", label: T("agencyLanguageFrench", "Français") },
      { value: "Anglais", label: T("agencyLanguageEnglish", "Anglais") },
      { value: "Espagnol", label: T("agencyLanguageSpanish", "Espagnol") },
      { value: "Italien", label: T("agencyLanguageItalian", "Italien") },
      { value: "Allemand", label: T("agencyLanguageGerman", "Allemand") },
      { value: "Portugais", label: T("agencyLanguagePortuguese", "Portugais") },
      { value: "Arabe", label: T("agencyLanguageArabic", "Arabe") },
      { value: "Berbère", label: T("agencyLanguageBerber", "Berbère") },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language]
  );

  const ajouterService = () => {
    setServicesRequis((prev) => [
      ...prev,
      { type_service: "", description: "", volume_estime: "", periode: "" },
    ]);
  };

  const supprimerService = (index) => {
    setServicesRequis((prev) => prev.filter((_, i) => i !== index));
  };

  const mettreAJourService = (index, champ, valeur) => {
    setServicesRequis((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [champ]: valeur };
      return copy;
    });
  };

  const handleCheckboxChange = (field, value) => {
    setAgencyInfo((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((item) => item !== value)
        : [...prev[field], value],
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAgencyInfo((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!agencyInfo.nom_agence.trim()) return T("vAgencyName", "Nom d'agence obligatoire");
    if (!agencyInfo.pays_origine.trim()) return T("vAgencyCountry", "Pays d'origine obligatoire");
    if (!agencyInfo.presentation_agence.trim()) return T("vAgencyPresentation", "Présentation obligatoire");
    if (!agencyInfo.type_services.length)
      return T("vAgencyServiceProvided", "Sélectionne au moins 1 type de service proposé");
    if (!agencyInfo.langues_parlees.length)
      return T("vAgencyLanguage", "Sélectionne au moins 1 langue parlée");
    if (!prenom.trim()) return T("vFirstName", "Prénom obligatoire");
    if (!nom.trim()) return T("vLastName", "Nom obligatoire");
    if (!email.trim()) return T("vEmail", "Email obligatoire");
    if (!telephone.trim()) return T("vPhone", "Téléphone obligatoire");

    for (let i = 0; i < servicesRequis.length; i++) {
      if (!servicesRequis[i].type_service) {
        const prefix = T("vRequiredServiceTypePrefix", "Type de service requis obligatoire (Service ");
        const suffix = T("vRequiredServiceTypeSuffix", ")");
        return `${prefix}${i + 1}${suffix}`;
      }
    }
    return null;
  };

  const resetForm = () => {
    setAgencyInfo({
      nom_agence: "",
      pays_origine: "",
      site_web: "",
      licence_numero: "",
      nb_clients_annuel: "",
      type_services: [],
      commission_souhaitee: "",
      volume_affaires_estime: "",
      experience_maroc: "",
      langues_parlees: [],
      presentation_agence: "",
    });
    setServicesRequis([{ type_service: "", description: "", volume_estime: "", periode: "" }]);

    if (!currentUser) {
      setEmail("");
      setNom("");
      setPrenom("");
      setTelephone("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }

    setLoading(true);
    try {
      const donnees = {
        ...agencyInfo,
        services_requis: servicesRequis,
        email,
        nom,
        prenom,
        telephone,
        nom_contact: `${prenom} ${nom}`.trim() || currentUser?.username || "",
      };

      const res = await api.post("/agency-partnership", donnees);

      if (res?.data?.success) {
        toast.success(T("agencySentOk", "Votre demande de partenariat a été enregistrée avec succès!"));
        setSuccess(true);
        resetForm();
      } else {
        toast.error(res?.data?.message || T("agencyGenericError", "Une erreur est survenue."));
      }
    } catch (error) {
      console.error("Erreur soumission:", error);
      notifyApiError(error, T("agencyNetworkError", "Une erreur est survenue. Veuillez réessayer plus tard."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="nouzha-premium-skin w-full min-h-screen relative overflow-hidden" style={{ background: "#FFF7ED" }}>
      {/* Background pattern */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${zelijPattern})`,
          backgroundRepeat: "repeat",
          backgroundSize: "400px",
          backgroundAttachment: "fixed",
          opacity: 0.06,
        }}
      />

      {/* ===== HERO SECTION ===== */}
      <div className="relative h-[60vh] min-h-[420px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600&q=80"
            alt="Agency Partnership"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FFF7ED] via-transparent to-transparent" />
        </div>

        <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold mb-6 backdrop-blur-md bg-white/20 text-white border border-white/30">
              <Handshake className="w-4 h-4" />
              {T("agencyProgramBadge", "Programme Partenaire")}
            </div>

            <h1 className="text-4xl md:text-6xl font-serif font-extrabold mb-4 text-white drop-shadow-2xl">
              {T("agencyPartnershipTitle", "Partenariat Agences")}
            </h1>

            <p className="text-lg md:text-xl max-w-3xl mx-auto leading-relaxed text-white/90 drop-shadow-lg">
              {T(
                "agencyPartnershipSubtitle",
                "Vous êtes une agence de voyage internationale ? Collaborez avec notre réseau d'agences marocaines pour offrir des services exceptionnels à vos clients au Maroc."
              )}
            </p>

            <motion.div
              className="mt-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <a
                href="#partnership-form"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-gradient-to-r from-orange-500 to-red-600 text-white font-semibold shadow-xl hover:from-orange-600 hover:to-red-700 transition-all hover:scale-105 duration-300"
              >
                {T("agencyBecomePartner", "Devenir partenaire")}
                <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 py-8" style={{ marginTop: "-2rem" }}>
        {/* ===== STATS STRIP ===== */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16"
        >
          {partnerStats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-white/95 backdrop-blur-sm rounded-2xl p-5 text-center shadow-lg border border-amber-100/50 hover:shadow-xl transition-all"
            >
              <div className="text-3xl font-extrabold text-orange-600">{stat.value}</div>
              <div className="text-sm text-gray-600 mt-1">{stat.label}</div>
            </motion.div>
          ))}
        </motion.section>

        {/* ===== BENEFITS SECTION with Images ===== */}
        <section className="mb-16">
          <div className="text-center mb-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-5 py-2 rounded-full text-sm font-semibold mb-4 border backdrop-blur-md border-orange-400/30 bg-amber-100/50 text-amber-700">
                {T("agencyBenefitsBadge", "Avantages partenaires")}
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-extrabold tracking-tight text-amber-900">
                {T("agencyWhyChoosePrefix", "Pourquoi")} <span className="text-orange-600">{T("agencyWhyChooseHighlight", "nous choisir")}</span> ?
              </h2>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, i) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-amber-100/50 hover:shadow-xl transition-all duration-300 group"
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${benefit.gradient} flex items-center justify-center shadow-lg mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  {benefit.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-sm text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ===== HOW IT WORKS with visual steps ===== */}
        <section className="mb-16">
          <div className="relative rounded-3xl overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1539020140153-e479b8c22e70?w=1400&q=80"
              alt="Morocco travel partnership"
              className="w-full h-64 md:h-80 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-orange-900/85 to-amber-800/80" />

            <div className="absolute inset-0 flex items-center">
              <div className="max-w-5xl mx-auto px-6 w-full">
                <h2 className="text-2xl md:text-3xl font-serif font-bold text-white mb-8 text-center">
                  {T("agencyHowToBecomePrefix", "Comment devenir")} <span className="text-orange-300">{T("agencyHowToBecomeHighlight", "partenaire")}</span> ?
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {partnershipSteps.map((item, i) => (
                    <motion.div
                      key={item.step}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.15 }}
                      className="text-center"
                    >
                      <div className="text-4xl font-extrabold text-orange-300/60 mb-2">{item.step}</div>
                      <h3 className="text-lg font-bold text-white mb-1">{item.title}</h3>
                      <p className="text-sm text-white/80">{item.desc}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== TESTIMONIALS ===== */}
        <section className="mb-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {partnerTestimonials.map((testimonial, i) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-amber-100/50"
              >
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-sm text-gray-700 italic mb-4">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-white font-bold text-sm">
                    {testimonial.name.split(" ").map((n) => n[0]).join("")}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">{testimonial.name}</p>
                    <p className="text-xs text-gray-500">{testimonial.agency}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ===== FORM ===== */}
        <div id="partnership-form">
          {success ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-xl p-8 text-center max-w-2xl mx-auto"
            >
              <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Handshake className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                {T("agencySuccessTitle", "Demande de partenariat envoyée!")}
              </h2>
              <p className="text-gray-600 mb-6">
                {T("agencySuccessBody", "Nous avons bien reçu votre demande. Notre équipe commerciale va l'étudier et vous contactera dans les plus brefs délais.")}
              </p>
              <button
                onClick={() => setSuccess(false)}
                className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl hover:from-orange-600 hover:to-red-700 transition-all font-semibold shadow-lg"
              >
                {T("agencyNewRequest", "Nouvelle demande")}
              </button>
            </motion.div>
          ) : (
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Form */}
              <motion.form
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                onSubmit={handleSubmit}
                className="lg:w-2/3"
              >
                <section className="mb-8 p-6 rounded-[1.75rem] border border-orange-100 bg-white/80 shadow-[0_8px_22px_rgba(15,23,42,0.06)]">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-9 h-9 rounded-xl bg-orange-500 text-white flex items-center justify-center shadow">
                      <Building className="w-4 h-4" />
                    </div>
                    <h2 className="text-2xl md:text-[1.9rem] leading-tight font-black tracking-tight text-[#111827]">
                      {T("agencyInfoTitle", "Informations sur votre agence")}
                    </h2>
                  </div>

                  {/* Infos base */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block font-medium mb-2 text-gray-700">
                      {T("agencyName", "Nom de votre agence")} *
                    </label>
                    <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70 hover:border-orange-300 transition-colors">
                      <Building className="mr-2 h-5 w-5 text-orange-500" />
                      <input
                        type="text"
                        name="nom_agence"
                        value={agencyInfo.nom_agence}
                        onChange={handleChange}
                        placeholder="Ex: Morocco Travel Partners"
                        className="w-full outline-none bg-transparent"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block font-medium mb-2 text-gray-700">
                      {T("agencyCountry", "Pays d'origine")} *
                    </label>
                    <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70 hover:border-orange-300 transition-colors">
                      <Globe className="mr-2 h-5 w-5 text-orange-500" />
                      <input
                        type="text"
                        name="pays_origine"
                        value={agencyInfo.pays_origine}
                        onChange={handleChange}
                        placeholder="Ex: France, Espagne, Allemagne..."
                        className="w-full outline-none bg-transparent"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block font-medium mb-2 text-gray-700">{T("agencyWebsite", "Site web")}</label>
                    <input
                      type="url"
                      name="site_web"
                      value={agencyInfo.site_web}
                      onChange={handleChange}
                      placeholder="https://www.votre-agence.com"
                      className="w-full border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70 focus:ring-2 focus:ring-orange-400 focus:border-orange-400 hover:border-orange-300 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block font-medium mb-2 text-gray-700">{T("agencyLicense", "Numéro de licence/agrément")}</label>
                    <input
                      type="text"
                      name="licence_numero"
                      value={agencyInfo.licence_numero}
                      onChange={handleChange}
                      placeholder="Numéro d'agrément officiel"
                      className="w-full border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70 focus:ring-2 focus:ring-orange-400 focus:border-orange-400 hover:border-orange-300 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block font-medium mb-2 text-gray-700">{T("agencyAnnualClients", "Nombre de clients annuel")}</label>
                    <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70 hover:border-orange-300 transition-colors">
                      <Users className="mr-2 h-5 w-5 text-orange-500" />
                      <select
                        name="nb_clients_annuel"
                        value={agencyInfo.nb_clients_annuel}
                        onChange={handleChange}
                        className="w-full outline-none bg-transparent"
                      >
                        <option value="">{T("selectOption", "Sélectionnez...")}</option>
                        <option value="moins-100">{T("clientsLess100", "Moins de 100 clients")}</option>
                        <option value="100-500">{T("clients100_500", "100 - 500 clients")}</option>
                        <option value="500-1000">{T("clients500_1000", "500 - 1000 clients")}</option>
                        <option value="1000-5000">{T("clients1000_5000", "1000 - 5000 clients")}</option>
                        <option value="plus-5000">{T("clientsMore5000", "Plus de 5000 clients")}</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block font-medium mb-2 text-gray-700">{T("agencyCommission", "Commission souhaitée (%)")}</label>
                    <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70 hover:border-orange-300 transition-colors">
                      <DollarSign className="mr-2 h-5 w-5 text-orange-500" />
                      <input
                        type="number"
                        name="commission_souhaitee"
                        value={agencyInfo.commission_souhaitee}
                        onChange={handleChange}
                        min="0"
                        max="50"
                        placeholder="Ex: 15"
                        className="w-full outline-none bg-transparent"
                      />
                    </div>
                  </div>
                  </div>
                </section>

                {/* Types services proposés */}
                <section className="mb-8 p-6 rounded-[1.75rem] border border-orange-100 bg-white/80 shadow-[0_8px_22px_rgba(15,23,42,0.06)]">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-orange-500 text-white flex items-center justify-center shadow">
                      <Briefcase className="w-4 h-4" />
                    </div>
                    <h3 className="text-2xl md:text-[1.65rem] leading-tight font-black tracking-tight text-[#111827]">
                      {T("agencyServicesProvided", "Types de services que vous proposez")} *
                    </h3>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {typesServices.map((service) => (
                      <label
                        key={service.value}
                        className={`flex items-center space-x-2 cursor-pointer p-3 rounded-xl border-2 transition-all duration-200 ${agencyInfo.type_services.includes(service.value)
                            ? "border-orange-400 bg-orange-50 shadow-sm"
                            : "border-gray-200 bg-white hover:border-orange-200"
                          }`}
                      >
                        <input
                          type="checkbox"
                          checked={agencyInfo.type_services.includes(service.value)}
                          onChange={() => handleCheckboxChange("type_services", service.value)}
                          className="rounded border-orange-300 text-orange-600 focus:ring-orange-500"
                        />
                        <span className="text-sm text-gray-700">{service.label}</span>
                      </label>
                    ))}
                  </div>
                </section>

                {/* Langues */}
                <section className="mb-8 p-6 rounded-[1.75rem] border border-orange-100 bg-white/80 shadow-[0_8px_22px_rgba(15,23,42,0.06)]">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-orange-500 text-white flex items-center justify-center shadow">
                      <Globe className="w-4 h-4" />
                    </div>
                    <h3 className="text-2xl md:text-[1.65rem] leading-tight font-black tracking-tight text-[#111827]">
                      {T("agencyTeamLanguages", "Langues parlées dans votre équipe")} *
                    </h3>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {languesDisponibles.map((langue) => (
                      <label
                        key={langue.value}
                        className={`flex items-center space-x-2 cursor-pointer p-3 rounded-xl border-2 transition-all duration-200 ${agencyInfo.langues_parlees.includes(langue.value)
                            ? "border-orange-400 bg-orange-50 shadow-sm"
                            : "border-gray-200 bg-white hover:border-orange-200"
                          }`}
                      >
                        <input
                          type="checkbox"
                          checked={agencyInfo.langues_parlees.includes(langue.value)}
                          onChange={() => handleCheckboxChange("langues_parlees", langue.value)}
                          className="rounded border-orange-300 text-orange-600 focus:ring-orange-500"
                        />
                        <span className="text-sm text-gray-700">{langue.label}</span>
                      </label>
                    ))}
                  </div>
                </section>

                <section className="mb-8 p-6 rounded-[1.75rem] border border-orange-100 bg-white/80 shadow-[0_8px_22px_rgba(15,23,42,0.06)]">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-orange-500 text-white flex items-center justify-center shadow">
                      <TrendingUp className="w-4 h-4" />
                    </div>
                    <h3 className="text-2xl md:text-[1.65rem] leading-tight font-black tracking-tight text-[#111827]">
                      {T("agencyBusinessProfile", "Profil de votre activité")}
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block font-medium mb-2 text-gray-700">{T("agencyEstimatedTurnover", "Volume d'affaires annuel estimé (EUR)")}</label>
                      <input
                        type="number"
                        name="volume_affaires_estime"
                        value={agencyInfo.volume_affaires_estime}
                        onChange={handleChange}
                        placeholder="Ex: 500000"
                        className="w-full border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70 focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                      />
                    </div>

                    <div>
                      <label className="block font-medium mb-2 text-gray-700">{T("agencyMoroccoExperience", "Expérience avec le Maroc")}</label>
                      <select
                        name="experience_maroc"
                        value={agencyInfo.experience_maroc}
                        onChange={handleChange}
                        className="w-full border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70"
                      >
                        <option value="">{T("selectOption", "Sélectionnez...")}</option>
                        <option value="aucune">{T("expNone", "Aucune expérience")}</option>
                        <option value="debutant">{T("expBeginner", "Débutant (moins de 2 ans)")}</option>
                        <option value="intermediaire">{T("expIntermediate", "Intermédiaire (2-5 ans)")}</option>
                        <option value="experimente">{T("expExperienced", "Expérimenté (plus de 5 ans)")}</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block font-medium mb-2 text-gray-700">
                      {T("agencyAgencyPresentation", "Présentation de votre agence")} *
                    </label>
                    <textarea
                      name="presentation_agence"
                      value={agencyInfo.presentation_agence}
                      onChange={handleChange}
                      rows="4"
                      placeholder={T("agencyPresentationPlaceholder", "Présentez votre agence, votre expertise, vos valeurs...")}
                      className="w-full border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70 focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                      required
                    />
                  </div>
                </section>

                {/* Services requis */}
                <section className="mb-8 p-6 rounded-[1.75rem] border border-orange-100 bg-white/80 shadow-[0_8px_22px_rgba(15,23,42,0.06)]">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl md:text-[1.65rem] leading-tight font-black tracking-tight text-[#111827] flex items-center gap-2">
                      <div className="w-9 h-9 rounded-xl bg-orange-500 text-white flex items-center justify-center shadow">
                        <Briefcase className="w-4 h-4" />
                      </div>
                      {T("agencyRequiredServicesTitle", "Services requis au Maroc")}
                    </h2>
                    <button
                      type="button"
                      onClick={ajouterService}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#0f172a] text-white text-sm font-black uppercase tracking-[0.08em] hover:bg-[#1e293b] transition-all"
                    >
                      <PlusCircle className="w-4 h-4" />
                      {T("agencyAddService", "Ajouter un service")}
                    </button>
                  </div>

                  {servicesRequis.map((service, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-5 mb-4 border border-orange-100 shadow-sm"
                    >
                      <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-white font-bold text-sm shadow">
                            {index + 1}
                          </div>
                          <h3 className="font-semibold text-orange-700">
                            {T("serviceLabel", "Service")} {index + 1}
                          </h3>
                        </div>
                        {servicesRequis.length > 1 && (
                          <button
                            type="button"
                            onClick={() => supprimerService(index)}
                            className="flex items-center gap-1 text-red-500 hover:text-red-600 transition-colors"
                          >
                            <Trash className="w-4 h-4" />
                            <span className="text-sm">{T("serviceDelete", "Supprimer")}</span>
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1 text-gray-700">
                            {T("serviceTypeRequired", "Type de service")} *
                          </label>
                          <div className="flex items-center border-2 border-orange-200 rounded-xl px-3 py-2 bg-white">
                            <Briefcase className="mr-2 h-4 w-4 text-orange-500" />
                            <select
                              value={service.type_service}
                              onChange={(e) => mettreAJourService(index, "type_service", e.target.value)}
                              className="w-full outline-none bg-transparent"
                              required
                            >
                              <option value="">{T("serviceTypeSelect", "Sélectionnez...")}</option>
                              {typesServices.map((type) => (
                                <option key={type.value} value={type.value}>
                                  {type.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-1 text-gray-700">
                            {T("serviceMonthlyVolume", "Volume estimé/mois")}
                          </label>
                          <input
                            type="text"
                            value={service.volume_estime}
                            onChange={(e) => mettreAJourService(index, "volume_estime", e.target.value)}
                            placeholder={T("serviceVolumePlaceholder", "Ex: 50 clients, 10 groupes...")}
                            className="w-full border-2 border-orange-200 rounded-xl px-3 py-2 bg-white"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-1 text-gray-700">
                            {T("servicePeriod", "Période souhaitée")}
                          </label>
                          <select
                            value={service.periode}
                            onChange={(e) => mettreAJourService(index, "periode", e.target.value)}
                            className="w-full border-2 border-orange-200 rounded-xl px-3 py-2 bg-white"
                          >
                            <option value="">{T("serviceTypeSelect", "Sélectionnez...")}</option>
                            <option value="toute-annee">{T("periodAllYear", "Toute l'année")}</option>
                            <option value="haute-saison">{T("periodHighSeason", "Haute saison uniquement")}</option>
                            <option value="basse-saison">{T("periodLowSeason", "Basse saison uniquement")}</option>
                            <option value="sur-demande">{T("periodOnDemand", "Sur demande")}</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-1 text-gray-700">
                            {T("serviceDetailedDesc", "Description détaillée")}
                          </label>
                          <textarea
                            value={service.description}
                            onChange={(e) => mettreAJourService(index, "description", e.target.value)}
                            placeholder={T("serviceDescPlaceholder", "Décrivez vos besoins spécifiques...")}
                            className="w-full border-2 border-orange-200 rounded-xl px-3 py-2 bg-white"
                            rows="2"
                          />
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </section>

                {/* Contact */}
                <section className="mb-8 p-6 rounded-[1.75rem] border border-orange-100 bg-white/80 shadow-[0_8px_22px_rgba(15,23,42,0.06)]">
                  <h2 className="text-2xl md:text-[1.65rem] leading-tight font-black tracking-tight text-[#111827] mb-4 flex items-center gap-2">
                    <div className="w-9 h-9 rounded-xl bg-orange-500 text-white flex items-center justify-center shadow">
                      <Mail className="w-4 h-4" />
                    </div>
                    {T("agencyContactTitle", "Contact principal")}
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-medium mb-2 text-gray-700">{T("firstName", "Prénom")} *</label>
                      <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                        <User className="mr-2 h-5 w-5 text-orange-500" />
                        <input
                          type="text"
                          value={prenom}
                          onChange={(e) => setPrenom(e.target.value)}
                          className="w-full outline-none bg-transparent"
                          placeholder={T("firstNamePh", "Votre prénom")}
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block font-medium mb-2 text-gray-700">{T("lastName", "Nom")} *</label>
                      <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                        <User className="mr-2 h-5 w-5 text-orange-500" />
                        <input
                          type="text"
                          value={nom}
                          onChange={(e) => setNom(e.target.value)}
                          className="w-full outline-none bg-transparent"
                          placeholder={T("lastNamePh", "Votre nom de famille")}
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block font-medium mb-2 text-gray-700">
                        {currentUser ? T("emailLabel", "Votre adresse e-mail") : T("emailLabelRequired", "Votre adresse e-mail *")}
                      </label>
                      <div
                        className={`flex items-center border-2 rounded-xl px-4 py-3 ${currentUser
                            ? "bg-gray-100 border-gray-200"
                            : "bg-white/70 border-orange-200 focus-within:ring-2 focus-within:ring-orange-400 focus-within:border-orange-400"
                          }`}
                      >
                        <Mail className="mr-2 h-5 w-5 text-orange-500" />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => !currentUser && setEmail(e.target.value)}
                          className={`w-full outline-none bg-transparent ${currentUser ? "cursor-not-allowed text-gray-600" : ""}`}
                          placeholder={T("contactEmailPh", "Ex: contact@votre-agence.com")}
                          readOnly={!!currentUser}
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block font-medium mb-2 text-gray-700">{T("phoneNumber", "Numéro de téléphone")} *</label>
                      <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                        <Phone className="mr-2 h-5 w-5 text-orange-500" />
                        <input
                          type="tel"
                          value={telephone}
                          onChange={(e) => setTelephone(e.target.value)}
                          className="w-full outline-none bg-transparent"
                          placeholder={T("contactPhonePh", "Ex: +33 1 23 45 67 89")}
                          required
                        />
                      </div>
                    </div>
                  </div>
                </section>

                {/* Submit */}
                <div className="text-center">
                  <motion.button
                    type="submit"
                    disabled={loading}
                    whileHover={{ scale: loading ? 1 : 1.02 }}
                    whileTap={{ scale: loading ? 1 : 0.98 }}
                    className={`px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-lg hover:shadow-xl ${loading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white"
                      }`}
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {T("sending", "Envoi en cours...")}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Send className="w-5 h-5" />
                        {T("agencySubmit", "Envoyer la demande de partenariat")}
                      </div>
                    )}
                  </motion.button>
                </div>
              </motion.form>

              {/* Sidebar */}
              <div className="lg:w-1/3">
                <div className="sticky top-28 space-y-6">
                  {/* Visual CTA Card */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                    className="relative rounded-3xl overflow-hidden shadow-xl"
                  >
                    <img
                      src={transportImg}
                      alt={T("agencySidebarImageAlt", "Morocco partnership")}
                      className="w-full h-56 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-orange-900/90 via-orange-900/40 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h3 className="text-lg font-bold text-white mb-1">{T("agencySidebarJoinTitle", "Rejoignez notre réseau")}</h3>
                      <p className="text-sm text-white/80">{T("agencySidebarJoinDesc", "150+ agences nous font déjà confiance")}</p>
                    </div>
                  </motion.div>

                  {/* Benefits side card */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                    className="bg-gradient-to-br from-orange-500 to-red-600 rounded-3xl shadow-xl p-6 text-white"
                  >
                    <h3 className="font-bold text-lg mb-4">{T("agencySidebarWhatYouGet", "Ce que vous obtenez")}</h3>
                    <ul className="space-y-3 text-sm text-white/90">
                      {[
                        T("agencySidebarBenefit1", "Dashboard partenaire dédié"),
                        T("agencySidebarBenefit2", "Tarifs préférentiels garantis"),
                        T("agencySidebarBenefit3", "Support multilingue 7j/7"),
                        T("agencySidebarBenefit4", "Formation sur nos produits"),
                        T("agencySidebarBenefit5", "Marketing co-brandé"),
                        T("agencySidebarBenefit6", "Paiements rapides"),
                      ].map((item, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-300 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </motion.div>

                  {/* Contact card */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.7 }}
                    className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-xl p-6 border border-amber-100/50"
                  >
                    <h3 className="font-bold text-gray-900 mb-3">{T("agencySidebarQuestions", "Des questions ?")}</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      {T("agencySidebarQuestionsDesc", "Notre équipe commerciale est à votre disposition pour tout renseignement.")}
                    </p>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-gray-700">
                        <Mail className="w-4 h-4 text-orange-500" />
                        partenariat@nouzha.ma
                      </div>
                      <div className="flex items-center gap-2 text-gray-700">
                        <Phone className="w-4 h-4 text-orange-500" />
                        +212 5XX XX XX XX
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgencyPartnership;
